import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Calendar,
  Clock,
  Package,
  User,
  Store,
  Banknote,
  QrCode as QrCodeIcon,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Download
} from 'lucide-react'
import QRCodeSVG from 'react-qr-code'
import { reservationService, Reservation } from '@/services/reservationService'
import useAppConfigStore from '@/stores/appConfigStore'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

const ReservationDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { systemConfig } = useAppConfigStore()
  
  const [reservation, setReservation] = useState<Reservation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadReservation = async () => {
      if (!id) return
      
      try {
        setLoading(true)
        const response = await reservationService.getById(id)
        
        if (response.success && response.data) {
          setReservation(response.data)
        } else {
          setError('Réservation non trouvée')
        }
      } catch (err) {
        console.error('Erreur lors du chargement de la réservation:', err)
        setError('Erreur lors du chargement de la réservation')
      } finally {
        setLoading(false)
      }
    }

    loadReservation()
  }, [id])

  const getStatusBadge = (status: Reservation['status']) => {
    switch (status) {
      case 'confirmed':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Confirmée
          </Badge>
        )
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200">
            <AlertCircle className="w-3 h-3 mr-1" />
            En attente
          </Badge>
        )
      case 'cancelled':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" />
            Annulée
          </Badge>
        )
      case 'completed':
        return (
          <Badge variant="default" className="bg-blue-100 text-blue-800 border-blue-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Terminée
          </Badge>
        )
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatCurrency = (amount: number | string) => {
    const currencyCode = systemConfig?.default_currency || 'MAD'
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode
    }).format(numAmount)
  }

  const getItemType = (reservation: Reservation) => {
    if (reservation.product_id) return 'Produit'
    if (reservation.offer_id) return 'Offre'
    return 'Inconnu'
  }

  const getItemId = (reservation: Reservation) => {
    return reservation.product_id || reservation.offer_id || 'N/D'
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Chargement..."
          subtitle="Chargement des détails de la réservation"
        />
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    )
  }

  if (error || !reservation) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Erreur"
          subtitle={error || 'Réservation non trouvée'}
        />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">
                Impossible de charger les détails de la réservation.
              </p>
              <Button onClick={() => navigate('/reservations')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour aux réservations
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        title={`Réservation #${reservation.reservation_id.substring(0, 8)}`}
        subtitle={`Créée le ${formatDate(reservation.created_at)}`}
        onBack={() => navigate('/reservations')}
        actions={[
          {
            label: 'Retour',
            icon: <ArrowLeft className="w-4 h-4" />,
            onClick: () => navigate('/reservations'),
            variant: 'outline'
          }
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="pt-6">
              <DetailSection title="Informations générales">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <User className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="font-medium">Client:</span>
                    </div>
                    <div className="pl-6">
                      {reservation.user_first_name || reservation.user_last_name ? (
                        <p className="font-semibold">
                          {reservation.user_first_name} {reservation.user_last_name}
                        </p>
                      ) : (
                        <p className="text-gray-500">Utilisateur #{reservation.user_id.substring(0, 8)}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Store className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="font-medium">Commerçant:</span>
                    </div>
                    <div className="pl-6">
                      <p className="font-semibold">{reservation.merchant_name || 'Non spécifié'}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Package className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="font-medium">Type:</span>
                    </div>
                    <div className="pl-6">
                      <p className="font-semibold">{getItemType(reservation)}</p>
                      <p className="text-sm text-gray-500">ID: {getItemId(reservation)}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Banknote className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="font-medium">Montant total:</span>
                    </div>
                    <div className="pl-6">
                      <p className="font-semibold text-lg">{formatCurrency(reservation.total_amount)}</p>
                    </div>
                  </div>

                  {reservation.notes && (
                    <div className="col-span-1 md:col-span-2 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <div className="flex items-start gap-2">
                        <Package size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Instructions spéciales</p>
                          <p className="text-sm text-amber-700 mt-0.5 whitespace-pre-wrap">{reservation.notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </DetailSection>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <DetailSection title="Statut et dates">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Statut</h4>
                      <div className="flex items-center">
                        {getStatusBadge(reservation.status)}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2">Quantité</h4>
                      <p className="text-2xl font-bold">{reservation.quantity}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Dates</h4>
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                          <span>Créée: {formatDate(reservation.created_at)}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                          <span>Modifiée: {formatDate(reservation.updated_at)}</span>
                        </div>
                        {reservation.pickup_time && (
                          <div className="flex items-center text-sm">
                            <Clock className="w-4 h-4 mr-2 text-gray-500" />
                            <span>Retrait: {formatDate(reservation.pickup_time)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </DetailSection>
            </CardContent>
          </Card>
        </div>

        {/* Right column - QR Code and actions */}
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <DetailSection title="QR Code">
                {reservation.qr_code_data ? (
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border text-center">
                      <div className="flex justify-center mb-4">
                        <QRCodeSVG
                          value={reservation.qr_code_data}
                          size={200}
                          bgColor="#FFFFFF"
                          fgColor="#000000"
                          level="H"
                          className="qr-code-svg"
                        />
                      </div>
                      <p className="text-sm font-mono break-all bg-gray-50 p-2 rounded border">
                        {reservation.qr_code_data}
                      </p>
                      <div className="flex justify-center mt-4 space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Function to download QR code as PNG
                            const svg = document.querySelector('.qr-code-svg')
                            if (svg) {
                              const svgData = new XMLSerializer().serializeToString(svg)
                              const canvas = document.createElement('canvas')
                              const ctx = canvas.getContext('2d')
                              const img = new Image()
                              img.onload = () => {
                                canvas.width = img.width
                                canvas.height = img.height
                                ctx?.drawImage(img, 0, 0)
                                const pngFile = canvas.toDataURL('image/png')
                                const downloadLink = document.createElement('a')
                                downloadLink.download = `qr-reservation-${reservation.reservation_id.substring(0, 8)}.png`
                                downloadLink.href = pngFile
                                downloadLink.click()
                              }
                              img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
                            }
                          }}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Télécharger
                        </Button>
                      </div>
                    </div>
                    
                    {reservation.qr_code_expires_at && (
                      <div className="text-sm text-gray-600">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          <span>Expire le: {formatDate(reservation.qr_code_expires_at)}</span>
                        </div>
                        {new Date(reservation.qr_code_expires_at) < new Date() && (
                          <Badge variant="destructive" className="mt-2">
                            QR Code expiré
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <QrCodeIcon className="h-16 w-16 mx-auto text-gray-300" />
                    <p className="mt-2 text-gray-500">Aucun QR code généré</p>
                  </div>
                )}
              </DetailSection>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <DetailSection title="Actions">
                <div className="space-y-3">
                  <Button className="w-full" onClick={() => window.print()}>
                    Imprimer les détails
                  </Button>
                  
                  {reservation.status === 'pending' && (
                    <Button variant="outline" className="w-full">
                      Confirmer la réservation
                    </Button>
                  )}
                  
                  {reservation.status === 'confirmed' && (
                    <Button variant="outline" className="w-full">
                      Marquer comme terminée
                    </Button>
                  )}
                  
                  {(reservation.status === 'pending' || reservation.status === 'confirmed') && (
                    <Button variant="destructive" className="w-full">
                      Annuler la réservation
                    </Button>
                  )}
                </div>
              </DetailSection>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default ReservationDetails